export interface PdfGenerationOptions {
  filename?: string;
  margin?: number;
  image?: { type: string; quality: number };
  html2canvas?: { scale: number; useCORS: boolean; allowTaint: boolean; logging: boolean };
  jsPDF?: { unit: 'mm' | 'pt' | 'px' | 'in' | 'cm' | 'ex' | 'em' | 'pc'; format: string; orientation: 'portrait' | 'p' | 'l' | 'landscape'; compress: boolean };
}

export async function generatePdfFromElement(
  element: HTMLElement,
  options: PdfGenerationOptions = {}
): Promise<void> {
  const {
    filename = `documento-${Date.now()}.pdf`,
    margin = 0,
    image = { type: 'jpeg', quality: 0.98 },
    html2canvas: html2canvasOptions = {
      scale: 1.5,
      useCORS: true,
      allowTaint: true,
      logging: false
    },
    jsPDF: jsPDFOptions = {
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait',
      compress: true
    }
  } = options;

  try {
    // Dynamic imports to avoid bundle bloat
    const [{ jsPDF }, html2canvasMod] = await Promise.all([
      import('jspdf'),
      import('html2canvas')
    ]);
    const html2canvas = (html2canvasMod as any).default || html2canvasMod;
    
    // Tipo fixo de imagem para evitar erros
    const IMAGE_TYPE: 'JPEG' = 'JPEG';

    // Set consistent width for A4 proportion
    const a4WidthPx = 900;
    const originalWidth = element.style.width;
    element.style.width = `${a4WidthPx}px`;

    // Ensure layout is applied before capture
    await new Promise<void>(resolve => 
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );

    // Capture element as canvas
    const canvas = await html2canvas(element, {
      ...html2canvasOptions,
      width: 900
    });

    // Restore original width
    element.style.width = originalWidth;

    // Create PDF
    const pdf = new jsPDF(jsPDFOptions);
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    
    // Margens profissionais para documentos jurídicos (15mm)
    const MARGIN_MM = 15;
    const DRAW_X = MARGIN_MM;
    const DRAW_Y = MARGIN_MM;
    const DRAW_W = pageWidth - (MARGIN_MM * 2);
    const DRAW_H = pageHeight - (MARGIN_MM * 2);
    
    // Calcular área útil para fatias
    const pxPerMm = canvas.width / pageWidth;
    const SLICE_H_PX = Math.round(DRAW_H * pxPerMm);
    
    const imgData = canvas.toDataURL('image/jpeg', image.quality);

    // Calcular altura da imagem mantendo proporção
    const imgHeight = (canvas.height / canvas.width) * DRAW_W;

    if (imgHeight <= DRAW_H) {
      // Single page - usar área útil com margens
      pdf.addImage(imgData, IMAGE_TYPE, DRAW_X, DRAW_Y, DRAW_W, imgHeight, undefined, 'FAST');
    } else {
      // Multiple pages - fatiar usando área útil
      const totalPages = Math.ceil(canvas.height / SLICE_H_PX);
      
      for (let page = 0; page < totalPages; page++) {
        const yPx = page * SLICE_H_PX;
        const sliceHeight = Math.min(SLICE_H_PX, canvas.height - yPx);
        
        // Create slice canvas
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;
        const ctx = pageCanvas.getContext('2d')!;
        
        ctx.drawImage(
          canvas,
          0, yPx, canvas.width, sliceHeight,
          0, 0, canvas.width, sliceHeight
        );

        if (page > 0) pdf.addPage();
        
        const sliceImgData = pageCanvas.toDataURL('image/jpeg', image.quality);
        // Calcular altura preservando proporção do slice
        const targetH = DRAW_W * (pageCanvas.height / pageCanvas.width);
        
        pdf.addImage(sliceImgData, IMAGE_TYPE, DRAW_X, DRAW_Y, DRAW_W, targetH, undefined, 'FAST');
      }
    }

    // Save PDF
    pdf.save(filename);
    
  } catch (error) {
    console.error('Erro ao gerar PDF:', error);
    throw new Error('Falha na geração do PDF');
  }
}